<template>
  <div :class="$style.shop_top">
    <div :class="$style.heading_area">
      <MoleculesConsumerHeading :class="$style.heading" title="OFFICIAL STORE" sub-title="直営店舗" align="left" />
      <div :class="$style.store_notify">
        <div>
          <p>※店舗によりお取り扱い商品・サービス内容が異なる場合がございます。</p>
          <p> 詳細は、店舗にお問い合わせください。</p>
        </div>
        <div>
          <p>※予告なしにお取り扱いがなくなる場合もございます。</p>
          <p> 在庫状況は店舗に直接ご確認いただき、来店いただくことをお薦めいたします。 </p>
        </div>
      </div>
    </div>

    <div ref="storeListRef" :class="$style.store_list_container">
      <div :class="$style.store_list_area">
        <StoreParallelList :stores="officialStores" />
      </div>
      <MoleculesConsumerPager
        :total-page="pageCount"
        :current-page="page"
        :update-page-mode="'emit'"
        @update-page="updateFilterValue"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createItemListJsonLD, createStoreJsonLD, useCustomMeta } from "~/composables/meta"
import { PublicStorePaginationQueryVariables } from "~/types/type.generated"
import { usePublicStorePaginationQuery } from "~/gql/urql.generated"

const route = useRoute()
const customMeta = useCustomMeta()
const perPage = 10
const storeListRef = ref<HTMLElement>()
const page = computed(() => {
  return typeof route.query.page === "string" ? +route.query.page : 1
})

const createVariables = (): PublicStorePaginationQueryVariables => {
  return {
    page: page.value,
    perPage,
    filter: {
      _operators: {
        type: {
          in: ["official", "popup"],
        },
      },
      is_status: true,
    },
    sort: "DISPLAY_ORDER_NUMBER_ASC",
  }
}

const storeSearchVariables = ref<PublicStorePaginationQueryVariables>(createVariables())
const { data: usePublicStorePaginationQueryRet, executeQuery: usePublicStorePaginationQueryExec } =
  usePublicStorePaginationQuery({
    variables: storeSearchVariables,
    requestPolicy: "cache-first",
    pause: true,
  })

const { data: storeListRes } = await useAsyncData("usePublicStorePaginationQuery", async () => {
  await usePublicStorePaginationQueryExec()
  return { data: usePublicStorePaginationQueryRet.value }
})
if (!usePublicStorePaginationQueryRet.value) usePublicStorePaginationQueryRet.value = storeListRes.value?.data

const officialStores = computed(() => {
  return usePublicStorePaginationQueryRet.value?.publicStorePagination?.items || []
})
const pageCount = computed(
  () => usePublicStorePaginationQueryRet.value?.publicStorePagination?.pageInfo?.pageCount || 1,
)

const createFilterQuery = (page: number) => {
  const query: { [key: string]: string } = { page: "1" }
  if (page) query.page = page.toString()
  return query
}

const updateFilterValue = (page: number) => {
  const query = createFilterQuery(page)
  navigateTo({ query })
}

const pageReExec = async () => {
  storeSearchVariables.value = createVariables()
  await nextTick(usePublicStorePaginationQueryExec)
  const element = storeListRef.value
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
    })
  }
}

watch(
  () => route.fullPath,
  async () => {
    await pageReExec()
  },
)

useHead(
  customMeta.fetch({
    title: "TENTIAL直営店一覧ページ",
    description: "TENTIAL[テンシャル]の直営店一覧ページです。お気軽に、ご連絡ください。",
    jsonLDInfo: createItemListJsonLD({
      count: officialStores.value?.length,
      items: officialStores.value?.map((store) => {
        return createStoreJsonLD(store)
      }),
    }),
  }),
)
</script>

<style lang="scss" scoped module>
.shop_top {
  @include container-lg;
  .heading_area {
    @include md {
      padding: 0;
    }
    .heading {
      padding-bottom: 1rem;
      @include md {
        padding-bottom: 2rem;
      }
    }
    .store_notify {
      display: flex;
      flex-flow: column;
      gap: 1rem;
      padding-bottom: 2rem;
      @include md {
        padding-bottom: 4rem;
      }
    }
  }
  .store_list_container {
    margin-bottom: 3rem;
    .store_list_area {
      margin-bottom: 2rem;
    }
  }
}
</style>
